import { useState, lazy, Suspense, useRef, useEffect } from 'react';
import { useOrderStore } from '@/store';
import { Order } from '@/types';
import { toast } from '@/components/UI/Toast';
import { OrderForm } from '@/components/Forms/OrderForm';
import { RushOrderForm } from '@/components/Forms/RushOrderForm';
import { Footer } from '@/components/Footer';
import { OverviewTab } from './OverviewTab';
import { PersonListEnhanced } from '@/components/Persons/PersonListEnhanced';
import { PersonHistory } from '@/components/Persons/PersonHistory';
import { ShiftManagement } from '@/components/Schedule/ShiftManagement';
import { QualityManagement } from '@/components/Quality/QualityManagement';
import { RushOrderManagement } from '@/components/RushOrder/RushOrderManagement';
import { ProductionReport } from '@/components/Production/ProductionReport';
import { BottleneckAnalysis } from '@/components/Analytics/BottleneckAnalysis';
import { MultiObjectTracking } from '@/components/Analytics/MultiObjectTracking';
import { GanttSkeleton } from '@/components/UI/Skeleton';

// 延遲載入甘特圖組件 - 使用命名導入的標準方式
const WorkOrderGantt = lazy(() => import('@/components/GanttChart/WorkOrderGantt'));
const PersonGantt = lazy(() => import('@/components/GanttChart/PersonGantt'));
const ResourceGantt = lazy(() => import('@/components/GanttChart/ResourceGantt'));

type TabType = 'overview' | 'orders' | 'rush' | 'persons' | 'report' | 'analytics' | 'tracking' | 'gantt' | 'personGantt' | 'resource' | 'quality';

export default function Dashboard() {
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [showOrderForm, setShowOrderForm] = useState(false);
  const [showRushOrderForm, setShowRushOrderForm] = useState(false);
  const [orderToEdit, setOrderToEdit] = useState<Order | null>(null);
  const [orderToDelete, setOrderToDelete] = useState<Order | null>(null);
  const [personViewType, setPersonViewType] = useState<'list' | 'history' | 'shift'>('list');

  // 優化：只在訂單管理頁面時才選取 orders，避免不必要的重新渲染
  const orders = useOrderStore((state) => state.orders);
  const deleteOrder = useOrderStore((state) => state.deleteOrder);

  // 處理編輯訂單
  const handleEditOrder = (order: Order) => {
    setOrderToEdit(order);
    setShowOrderForm(true);
  };

  // 處理刪除訂單
  const handleDeleteOrder = () => {
    if (!orderToDelete) return;

    deleteOrder(orderToDelete.id);
    toast.success(
      '訂單已刪除',
      `訂單 ${orderToDelete.id} 已成功刪除`,
      3000
    );
    setOrderToDelete(null);
  };

  // 開啟新增訂單表單時清除編輯狀態
  const handleOpenAddForm = () => {
    setOrderToEdit(null);
    setShowOrderForm(true);
  };

  // 關閉表單時清除編輯狀態
  const handleCloseForm = () => {
    setShowOrderForm(false);
    setOrderToEdit(null);
  };

  // 標籤順序（用於方向鍵導航）
  const tabs: TabType[] = ['overview', 'orders', 'rush', 'persons', 'report', 'analytics', 'tracking', 'gantt', 'personGantt', 'resource', 'quality'];

  // 標籤 ref 映射
  const tabRefs = useRef<Record<TabType, HTMLButtonElement | null>>({} as Record<TabType, HTMLButtonElement | null>);

  // 當 activeTab 改變時，確保焦點移動到對應的標籤
  useEffect(() => {
    const activeTabButton = tabRefs.current[activeTab];
    if (activeTabButton) {
      activeTabButton.focus();
    }
  }, [activeTab]);

  // 處理標籤的鍵盤事件
  const handleTabKeyDown = (e: React.KeyboardEvent, currentTab: TabType) => {
    let newIndex = tabs.indexOf(currentTab);

    switch (e.key) {
      case 'ArrowLeft':
        e.preventDefault();
        newIndex = newIndex > 0 ? newIndex - 1 : tabs.length - 1;
        break;
      case 'ArrowRight':
        e.preventDefault();
        newIndex = newIndex < tabs.length - 1 ? newIndex + 1 : 0;
        break;
      case 'Home':
        e.preventDefault();
        newIndex = 0;
        break;
      case 'End':
        e.preventDefault();
        newIndex = tabs.length - 1;
        break;
      default:
        return;
    }

    setActiveTab(tabs[newIndex]);
  };

  return (
    <div className="min-h-screen bg-[#f8fafc] grid-pattern">
      {/* Header - Professional Industrial Style */}
      <header className="bg-white border-b border-[#e2e8f0]">
        <div className="w-full px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold text-[#0f172a]">
                製造排程系統
              </h1>
              <p className="text-sm text-[#64748b] mt-0.5">
                Manufacturing Scheduling System POC Demo
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <span className="px-3 py-1.5 text-xs font-medium bg-[#0066cc]/10 text-[#0066cc] rounded border border-[#0066cc]/20">
                靜態部署
              </span>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation Tabs */}
      <nav
        className="sticky top-0 z-40 bg-white/95 backdrop-blur-sm border-b border-[#e2e8f0] transition-all duration-300 shadow-sm"
        role="navigation"
        aria-label="主要導航"
      >
        <div className="w-full px-4 sm:px-6 lg:px-8">
          {/* 行動版水平滾動導航 */}
          <div
            className="flex space-x-1 overflow-x-auto scrollbar-hide -mx-4 px-4 sm:mx-0 sm:px-0"
            role="tablist"
            aria-label="頁面標籤"
          >
            {tabs.map((tab) => {
              const tabLabels: Record<TabType, string> = {
                overview: '總覽',
                orders: '訂單管理',
                rush: '插單管理',
                persons: '人員資源',
                report: '生產報工',
                analytics: '瓶頸分析',
                tracking: '物件追蹤',
                gantt: '工單甘特圖',
                personGantt: '人員排程',
                resource: '資源甘特圖',
                quality: '品質管理'
              };

              return (
                <button
                  key={tab}
                  ref={(el) => tabRefs.current[tab] = el}
                  onClick={() => setActiveTab(tab)}
                  onKeyDown={(e) => handleTabKeyDown(e, tab)}
                  className={`nav-tab whitespace-nowrap ${activeTab === tab ? 'active' : ''}`}
                  role="tab"
                  aria-selected={activeTab === tab}
                  aria-controls={`${tab}-panel`}
                  aria-label={tabLabels[tab]}
                  tabIndex={activeTab === tab ? 0 : -1}
                >
                  {tabLabels[tab]}
                </button>
              );
            })}
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main
        className="w-full px-4 py-8 sm:px-6 lg:px-8"
        role="main"
        aria-label="主要內容區域"
        id="main-content"
      >
        <div className="page-content-wrapper">
          {activeTab === 'overview' && (
            <div
              className="page-content"
              role="tabpanel"
              id="overview-panel"
              aria-labelledby="總覽"
              tabIndex={0}
            >
              <OverviewTab onNavigate={setActiveTab} />
            </div>
          )}

          {activeTab === 'orders' && (
            <div
              className="page-content space-y-6"
              role="tabpanel"
              id="orders-panel"
              aria-labelledby="訂單管理"
              tabIndex={0}
            >
            {/* 統計概覽卡片 */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow">
                <div className="text-sm font-medium text-blue-700 mb-2">總訂單數</div>
                <div className="text-3xl font-black text-blue-600">{orders.length}</div>
              </div>
              <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow">
                <div className="text-sm font-medium text-amber-700 mb-2">進行中</div>
                <div className="text-3xl font-black text-amber-600">{orders.filter(o => o.status === '進行中').length}</div>
              </div>
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow">
                <div className="text-sm font-medium text-green-700 mb-2">已完成</div>
                <div className="text-3xl font-black text-green-600">{orders.filter(o => o.status === '已完成').length}</div>
              </div>
              <div className="bg-gradient-to-br from-slate-50 to-zinc-50 border border-slate-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow">
                <div className="text-sm font-medium text-slate-700 mb-2">待處理</div>
                <div className="text-3xl font-black text-slate-600">{orders.filter(o => o.status === '待處理').length}</div>
              </div>
            </div>

            {/* 訂單列表 */}
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
              <div className="px-6 py-5 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-white flex items-center justify-between">
                <h2 className="text-xl font-bold text-slate-800">📦 訂單列表</h2>
                <div className="flex gap-3">
                  <button onClick={handleOpenAddForm} className="px-5 py-2.5 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 text-white rounded-lg transition-all text-sm font-semibold shadow-md shadow-blue-500/20 hover:shadow-lg active:scale-95">
                    ➕ 新增訂單
                  </button>
                  <button onClick={() => setShowRushOrderForm(true)} className="px-5 py-2.5 bg-gradient-to-r from-red-500 to-rose-500 hover:from-red-600 hover:to-rose-600 text-white rounded-lg transition-all text-sm font-semibold shadow-md shadow-red-500/20 hover:shadow-lg active:scale-95">
                    ⚡ 插單
                  </button>
                </div>
              </div>
              <OrderList orders={orders} onEdit={handleEditOrder} onDelete={setOrderToDelete} onOpenAddForm={handleOpenAddForm} />
            </div>
          </div>
        )}

        {activeTab === 'rush' && (
          <div className="page-content"
              role="tabpanel"
              id="rush-panel"
              aria-labelledby="插單管理"
              tabIndex={0}>
            <RushOrderManagement />
          </div>
        )}

        {activeTab === 'report' && (
          <div className="page-content"
              role="tabpanel"
              id="report-panel"
              aria-labelledby="生產報工"
              tabIndex={0}>
            <ProductionReport />
          </div>
        )}

        {activeTab === 'analytics' && (
          <div className="page-content"
              role="tabpanel"
              id="analytics-panel"
              aria-labelledby="瓶頸分析"
              tabIndex={0}>
            <BottleneckAnalysis />
          </div>
        )}

        {activeTab === 'tracking' && (
          <div className="page-content"
              role="tabpanel"
              id="tracking-panel"
              aria-labelledby="物件追蹤"
              tabIndex={0}>
            <MultiObjectTracking />
          </div>
        )}

        {activeTab === 'persons' && (
          <div className="page-content"
              role="tabpanel"
              id="persons-panel"
              aria-labelledby="人員資源"
              tabIndex={0}>
            {personViewType === 'list' ? (
              <PersonListEnhanced
                onViewHistory={() => setPersonViewType('history')}
                onViewShift={() => setPersonViewType('shift')}
              />
            ) : personViewType === 'history' ? (
              <PersonHistory onBack={() => setPersonViewType('list')} />
            ) : (
              <ShiftManagement />
            )}
          </div>
        )}

        {activeTab === 'gantt' && (
          <div className="page-content"
              role="tabpanel"
              id="gantt-panel"
              aria-labelledby="工單甘特圖"
              tabIndex={0}>
            <h2 className="text-lg font-semibold text-[#0f172a] mb-4 px-4">工單甘特圖</h2>
            <Suspense fallback={<GanttSkeleton />}>
              <WorkOrderGantt />
            </Suspense>
          </div>
        )}

        {activeTab === 'personGantt' && (
          <div className="page-content"
              role="tabpanel"
              id="personGantt-panel"
              aria-labelledby="人員排程"
              tabIndex={0}>
            <Suspense fallback={<GanttSkeleton />}>
              <PersonGantt />
            </Suspense>
          </div>
        )}

        {activeTab === 'resource' && (
          <div className="page-content"
              role="tabpanel"
              id="resource-panel"
              aria-labelledby="資源甘特圖"
              tabIndex={0}>
            <Suspense fallback={<GanttSkeleton />}>
              <ResourceGantt />
            </Suspense>
          </div>
        )}

        {activeTab === 'quality' && (
          <div className="page-content"
              role="tabpanel"
              id="quality-panel"
              aria-labelledby="品質管理"
              tabIndex={0}>
            <QualityManagement />
          </div>
        )}
        </div>
      </main>

      {/* 表單對話框 */}
      {showOrderForm && <OrderForm onClose={handleCloseForm} orderToEdit={orderToEdit} />}
      {showRushOrderForm && <RushOrderForm onClose={() => setShowRushOrderForm(false)} />}

      {/* 刪除確認對話框 */}
      {orderToDelete && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in">
          <div className="bg-white border-2 border-red-200 rounded-2xl p-8 max-w-md w-full shadow-2xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-rose-600 rounded-xl flex items-center justify-center shadow-lg shadow-red-500/30">
                <span className="text-white text-xl">🗑️</span>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-slate-800">確認刪除訂單</h2>
                <p className="text-sm text-slate-500">此操作無法復原</p>
              </div>
            </div>

            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
              <div className="text-sm text-slate-700 mb-2">
                <span className="font-semibold">訂單編號：</span>
                <span className="text-red-700 font-bold">{orderToDelete.id}</span>
              </div>
              <div className="text-sm text-slate-700 mb-2">
                <span className="font-semibold">產品名稱：</span>
                <span>{orderToDelete.productName}</span>
              </div>
              <div className="text-sm text-slate-700 mb-2">
                <span className="font-semibold">客戶：</span>
                <span>{orderToDelete.customer}</span>
              </div>
              <div className="text-sm text-slate-700">
                <span className="font-semibold">數量：</span>
                <span>{orderToDelete.totalQuantity.toLocaleString()}</span>
              </div>
            </div>

            <p className="text-slate-600 mb-6">
              您確定要刪除此訂單嗎？此操作將永久刪除該訂單及其所有相關的工單資料。
            </p>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setOrderToDelete(null)}
                className="px-6 py-2.5 bg-gradient-to-r from-slate-100 to-slate-200 text-slate-700 rounded-xl hover:from-slate-200 hover:to-slate-300 transition-all text-sm font-semibold shadow-sm hover:shadow-md active:scale-95"
              >
                取消
              </button>
              <button
                onClick={handleDeleteOrder}
                className="px-6 py-2.5 bg-gradient-to-r from-red-600 to-rose-600 text-white rounded-xl hover:from-red-700 hover:to-rose-700 transition-all text-sm font-semibold shadow-lg shadow-red-500/30 hover:shadow-xl hover:shadow-red-500/40 active:scale-95"
              >
                🗑️ 確認刪除
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <Footer />
    </div>
  );
}

// Order List
function OrderList({ orders, onEdit, onDelete, onOpenAddForm }: {
  orders: Order[];
  onEdit: (order: Order) => void;
  onDelete: (order: Order) => void;
  onOpenAddForm?: () => void;
}) {
  if (orders.length === 0) {
    return (
      <div className="bg-gradient-to-br from-slate-50 to-white border-2 border-slate-200 rounded-xl p-12 text-center shadow-sm">
        <div className="text-6xl mb-4">📭</div>
        <h3 className="text-xl font-bold text-slate-800 mb-2">暫無訂單資料</h3>
        <p className="text-slate-600 mb-6 max-w-md mx-auto">
          開始建立您的第一個訂單，系統將自動生成工單並安排生產排程
        </p>
        {onOpenAddForm && (
          <button
            onClick={onOpenAddForm}
            className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-500 hover:from-blue-700 hover:to-indigo-600 text-white rounded-xl transition-all text-sm font-semibold shadow-lg shadow-blue-500/30 hover:shadow-xl hover:shadow-blue-500/40 active:scale-95"
          >
            <span className="text-lg">➕</span>
            建立第一個訂單
          </button>
        )}
      </div>
    );
  }

  const getStatusConfig = (status: string) => {
    switch (status) {
      case '已完成':
        return {
          bg: 'bg-gradient-to-r from-green-100 to-emerald-100',
          text: 'text-green-700',
          border: 'border-green-300',
          dot: 'bg-green-500'
        };
      case '進行中':
        return {
          bg: 'bg-gradient-to-r from-amber-100 to-orange-100',
          text: 'text-amber-700',
          border: 'border-amber-300',
          dot: 'bg-amber-500'
        };
      case '已延遲':
        return {
          bg: 'bg-gradient-to-r from-red-100 to-rose-100',
          text: 'text-red-700',
          border: 'border-red-300',
          dot: 'bg-red-500'
        };
      default:
        return {
          bg: 'bg-gradient-to-r from-slate-100 to-zinc-100',
          text: 'text-slate-700',
          border: 'border-slate-300',
          dot: 'bg-slate-500'
        };
    }
  };

  const getProgressColor = (progress: number) => {
    if (progress >= 80) return 'from-green-500 to-emerald-400';
    if (progress >= 50) return 'from-blue-500 to-cyan-400';
    if (progress >= 20) return 'from-amber-500 to-orange-400';
    return 'from-slate-400 to-zinc-400';
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-gradient-to-r from-slate-50 to-slate-100 border-b-2 border-slate-200">
          <tr>
            <th className="px-5 py-4 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">訂單編號</th>
            <th className="px-5 py-4 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">產品名稱</th>
            <th className="px-5 py-4 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">客戶</th>
            <th className="px-5 py-4 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">數量</th>
            <th className="px-5 py-4 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">預計交期</th>
            <th className="px-5 py-4 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">進度</th>
            <th className="px-5 py-4 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">狀態</th>
            <th className="px-5 py-4 text-center text-xs font-bold text-slate-600 uppercase tracking-wider">操作</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {orders.map((order, index) => {
            const statusConfig = getStatusConfig(order.status);
            const progressColor = getProgressColor(order.progress);

            return (
              <tr key={order.id} className={`hover:bg-blue-50/50 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-slate-50/30'}`}>
                <td className="px-5 py-4 whitespace-nowrap">
                  <span className="text-sm font-bold text-blue-600 hover:text-blue-700 cursor-pointer hover:underline">
                    {order.id}
                  </span>
                </td>
                <td className="px-5 py-4 text-sm font-medium text-slate-700">{order.productName}</td>
                <td className="px-5 py-4 text-sm text-slate-600">{order.customer}</td>
                <td className="px-5 py-4 whitespace-nowrap text-sm font-semibold text-slate-700">{order.totalQuantity.toLocaleString()}</td>
                <td className="px-5 py-4 whitespace-nowrap text-sm text-slate-600">
                  {new Date(order.plannedDeliveryDate).toLocaleDateString('zh-TW', { year: 'numeric', month: '2-digit', day: '2-digit' })}
                </td>
                <td className="px-5 py-4">
                  <div className="flex items-center gap-3">
                    <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden max-w-[120px]">
                      <div className={`h-full bg-gradient-to-r ${progressColor} transition-all duration-500`} style={{ width: `${order.progress}%` }}></div>
                    </div>
                    <span className="text-sm font-bold text-slate-700 min-w-[40px]">{order.progress}%</span>
                  </div>
                </td>
                <td className="px-5 py-4 whitespace-nowrap">
                  <span className={`inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-bold shadow-sm border ${statusConfig.bg} ${statusConfig.text} ${statusConfig.border}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${statusConfig.dot}`}></span>
                    {order.status}
                  </span>
                </td>
                <td className="px-5 py-4 whitespace-nowrap">
                  <div className="flex items-center justify-center gap-2">
                    <button
                      onClick={() => onEdit(order)}
                      className="p-2 bg-gradient-to-r from-blue-100 to-blue-50 hover:from-blue-200 hover:to-blue-100 text-blue-700 rounded-lg transition-all text-xs font-semibold shadow-sm hover:shadow-md active:scale-95"
                      title="編輯訂單"
                      aria-label={`編輯訂單 ${order.id}`}
                    >
                      <span aria-hidden="true">✏️</span> 編輯
                    </button>
                    <button
                      onClick={() => onDelete(order)}
                      className="p-2 bg-gradient-to-r from-red-100 to-red-50 hover:from-red-200 hover:to-red-100 text-red-700 rounded-lg transition-all text-xs font-semibold shadow-sm hover:shadow-md active:scale-95"
                      title="刪除訂單"
                      aria-label={`刪除訂單 ${order.id}`}
                    >
                      <span aria-hidden="true">🗑️</span> 刪除
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
